import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  CssBaseline,
  Box,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import MiniDrawer from "./MiniDrawer";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { trTR } from "@mui/x-data-grid/locales";
import { useIntl, IntlProvider, FormattedMessage } from "react-intl";
import messages from "../../locales";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../Themes/themes";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

const generateAcademicYears = () => {
  const years = [];
  const current = new Date();
  const startYear = 2024;

  for (let i = 0; i < 2; i++) {
    const start = startYear + i;
    const end = start + 1;
    years.push(`${start} - ${end}`);
  }
  return years;
};

const ReportList: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [schoolName, setSchoolName] = useState<string>("");
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const location = useLocation();
  const initialSchoolId = location.state?.schoolId;
  const language = location.state?.language;
  const [noData, setNoData] = useState(false);

  const [selectedYear, setSelectedYear] = useState<string>("");
  const [academicYears, setAcademicYears] = useState<string[]>([]);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        let response;

        if (initialSchoolId === 9999) {
          response = await axios.get(
            "https://eyelexia.eyesoft.com.tr/api/api/academicyears"
          );
        } else {
          response = await axios.get(
            "https://eyelexia.eyesoft.com.tr/api/api/academicyears",
            {
              params: {
                type: "school",
                school_id: initialSchoolId,
              },
            }
          );
        }

        const years = response.data.map(
          (item: { academic_year: string | number }) => {
            const start = item.academic_year.toString();
            const end = (parseInt(start) + 1).toString();
            return `${start}-${end}`;
          }
        );
        setAcademicYears(years);
      } catch (error) {
        console.error("Akademik yıllar alınamadı:", error);
      }
    };

    fetchAcademicYears();
  }, [initialSchoolId]);
  useEffect(() => {
    if (academicYears.length === 0) return;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
    const endYear = startYear + 1;

    const currentEduYear = `${startYear}-${endYear}`;

    if (academicYears.includes(currentEduYear)) {
      setSelectedYear(currentEduYear);
    } else {
      setSelectedYear(academicYears[0]);
    }
  }, [academicYears]);
  useEffect(() => {
    if (language) {
      localStorage.setItem("selectedLanguage", language);
    }
  }, [language]);

  const [schoolId, setSchoolId] = useState(initialSchoolId);
  const intl = useIntl();

  useEffect(() => {
    if (initialSchoolId) {
      setSchoolId(initialSchoolId);
      localStorage.setItem("schoolId", initialSchoolId.toString());
    }
  }, [initialSchoolId]);

  /*useEffect(() => {
    const fetchSchoolData = async () => {
      setLoadingSchool(true);
      if (schoolId === 9999) {
        const defaultSchoolName = intl.formatMessage({ id: "meb" });
        setSchoolName(defaultSchoolName);
        localStorage.setItem("schoolName", defaultSchoolName);
        setLoadingSchool(false);
      } else {
        console.log("SchoolID: " + schoolId);
        try {
          const response = await axios.get(
            `https://eyelexia.eyesoft.com.tr/api/api/get-school?id=${schoolId}`
          );
          setSchoolName(response.data.Name);
          localStorage.setItem("schoolName", response.data.Name);
        } catch (error) {
          console.error("Error fetching school data:", error);
        } finally {
          setLoadingSchool(false);
        }
      }
    };

    fetchSchoolData();
  }, [schoolId]);*/

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = event.target.value;
    setSelectedYear(year); // Yıl değiştiğinde seçilen yıl bilgisini güncelle
  };

  useEffect(() => {
    const fetchStudentsData = async () => {
      if (!schoolId || !selectedYear) return;

      const [startYearStr, endYearStr] = selectedYear
        .split("-")
        .map((s) => s.trim());

      const startDate = `${startYearStr}-08-01`;
      const endDate = `${endYearStr}-07-31`;
      setLoading(true);

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      const currentEduStartYear =
        currentMonth >= 8 ? currentYear : currentYear - 1;
      const isCurrentEducationYear =
        parseInt(startYearStr) === currentEduStartYear;

      try {
        let response;
        if (isCurrentEducationYear) {
          if (schoolId === 9999) {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&type=all_or_none`
            );
          } else {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&school_id=${schoolId}&type=with_or_none`
            );
          }
        } else {
          if (schoolId === 9999) {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&type=all`
            );
          } else {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&school_id=${schoolId}&type=with`
            );
          }
        }

        if (Array.isArray(response.data) && response.data.length === 0) {
          setNoData(true);
          setStudents([]); // boş dizi olarak ayarla
        } else if (Array.isArray(response.data)) {
          setNoData(false);
          setStudents(response.data);
          localStorage.setItem("students", JSON.stringify(response.data));
        } else {
          setNoData(true);
          setStudents([]); // güvenli boş dizi
        }
        localStorage.setItem("students", JSON.stringify(response.data));
      } catch (error) {
        console.error("Error fetching students data:", error);
      } finally {
        setLoading(false);
      }
    };

    localStorage.removeItem("students");
    fetchStudentsData();
  }, [schoolId, selectedYear]);

  useEffect(() => {
    const storedSchoolName = localStorage.getItem("schoolName");
    if (storedSchoolName) {
      setSchoolName(storedSchoolName);
    }
  }, []);

  useEffect(() => {
    const storedStudents = localStorage.getItem("students");
    if (storedStudents) {
      setStudents(JSON.parse(storedStudents));
      setLoading(false);
    }
  }, []);
  const getDyslexiaLevelText = (value: number | null | undefined): string => {
    if (value == null) return "-";

    const percent = Math.round(value * 100);

    if (percent >= 0 && percent < 25)
      return intl.formatMessage({ id: "very_low" });

    if (percent >= 25 && percent < 50) return intl.formatMessage({ id: "low" });

    if (percent >= 50 && percent < 75)
      return intl.formatMessage({ id: "high" });

    if (percent >= 75) return intl.formatMessage({ id: "very_high" });

    return "-";
  };
  const getSessionStatusText = (status: string) => {
    switch (status) {
      case "1":
        return `${intl.formatMessage({ id: "dyslexia" })}`;
      default:
        return `${intl.formatMessage({ id: "incorrectsession" })}`;
    }
  };
  const formattedData = useMemo(() => {
    let filtered = students.map((student) => ({
      id: student.ID,
      sessionstatus: getSessionStatusText(
        student.SessionStatus || student.session_status
      ),
      recordDateTime: dayjs(student.record_datetime).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
      ...student,
    }));

    if (selectedYear) {
      const [startYear, endYear] = selectedYear.split("-").map(Number);
      const startDate = dayjs(`${startYear}-08-01`);
      const endDate = dayjs(`${endYear}-07-31`).endOf("day");

      filtered = filtered.filter((row) => {
        const recordDateTime = dayjs(row.recordDateTime);
        return (
          recordDateTime.isAfter(startDate) && recordDateTime.isBefore(endDate)
        );
      });
    }

    return filtered;
  }, [students, selectedYear]);

  const [filteredRows, setFilteredRows] = useState(formattedData);

  useEffect(() => {
    const searchRegex = new RegExp(searchQuery, "i");

    const filtered = formattedData.filter((row) => {
      const matchSearch = searchRegex.test(row.Name);

      if (!selectedYear) return matchSearch;

      const [startYearStr, endYearStr] = selectedYear.split("-");
      const startDate = dayjs(`${startYearStr}-08-01`);
      const endDate = dayjs(`${endYearStr}-07-31`).endOf("day");

      const recordDate = dayjs(row.record_datetime);
      const isInRange =
        recordDate.isValid() &&
        recordDate.isBetween(startDate, endDate, null, "[]");

      const isMissingScan =
        (!recordDate.isValid() || !row.RecordDateTime) &&
        dayjs(row.RecordDate).isBetween(startDate, endDate, null, "[]");

      return matchSearch && (isInRange || isMissingScan);
    });

    setFilteredRows(filtered);
  }, [searchQuery, selectedYear, formattedData]);

  const getLoanStats = (classLevel: string) => {
    const classRows = filteredRows.filter(
      (row) => row.session_class === classLevel
    );

    const branchStats: Record<
      string,
      {
        total: number;
        dyslexiaCount: number;
        correctSessionCount: number;
        validDyslexiaBase: number;
      }
    > = {};

    classRows.forEach((student) => {
      const branch = student.student_branch;
      const dyslexiaPossibility = parseFloat(student.dyslexia_possibility);
      const isCorrectSession =
        student.session_type === "1" || student.session_type === 1;

      if (!branchStats[branch]) {
        branchStats[branch] = {
          total: 0,
          dyslexiaCount: 0,
          correctSessionCount: 0,
          validDyslexiaBase: 0,
        };
      }

      branchStats[branch].total += 1;

      if (isCorrectSession) {
        branchStats[branch].correctSessionCount += 1;
        branchStats[branch].validDyslexiaBase += 1;

        if (dyslexiaPossibility > 0.5) {
          branchStats[branch].dyslexiaCount += 1;
        }
      }
    });

    const stats = Object.entries(branchStats).map(([branch, data], index) => ({
      id: index,
      branch,
      total: data.total,
      dyslexiaCount: data.dyslexiaCount,
      correctSessionCount: data.correctSessionCount,
      dyslexiaPercentage:
        data.validDyslexiaBase === 0
          ? 0
          : Math.round((data.dyslexiaCount / data.validDyslexiaBase) * 100),
      correctSessionPercentage:
        data.total === 0
          ? 0
          : Math.round((data.correctSessionCount / data.total) * 100),
    }));

    return stats;
  };

  const calculateTotals = (stats: any[]) => {
    return stats.reduce(
      (acc, curr) => {
        acc.total += curr.total;
        acc.dyslexiaCount += curr.dyslexiaCount;
        acc.correctSessionCount += curr.correctSessionCount;
        acc.validDyslexiaBase += curr.correctSessionCount;
        return acc;
      },
      {
        total: 0,
        dyslexiaCount: 0,
        correctSessionCount: 0,
        validDyslexiaBase: 0,
      }
    );
  };

  const classLevels = Array.from(
    new Set(filteredRows.map((row) => row.session_class))
  );
  const statsByClassLevel = classLevels.map((classLevel) => {
    const stats = getLoanStats(classLevel);
    const totals = calculateTotals(stats);
    return {
      classLevel,
      stats,
      totals,
    };
  });

  const overallTotals = calculateTotals(
    statsByClassLevel.flatMap((item) => item.stats)
  );

  const calculatePercentage = (count: number, total: number) => {
    return total === 0 ? 0 : Math.round((count / total) * 100);
  };

  const columns: GridColDef[] = [
    {
      field: "branch",
      headerName: intl.formatMessage({ id: "branch" }),
      flex: 1,
    },
    {
      field: "total",
      headerName: intl.formatMessage({ id: "totalStudents" }),
      flex: 1,
    },
    {
      field: "dyslexiaCount",
      headerName: intl.formatMessage({ id: "dyslexiaCount" }),
      flex: 1,
    },
    {
      field: "dyslexiaPercentage",
      headerName: intl.formatMessage({ id: "dyslexiaRatio" }),
      flex: 1,
      renderCell: (params) => `%${params.value}`,
    },
  ];

  return (
    <IntlProvider
      locale={language}
      messages={messages[language] as unknown as Record<string, string>}
    >
      <MiniDrawer userName={schoolName}>
        <ThemeProvider theme={theme}>
          <Container
            component="main"
            maxWidth="xl"
            sx={{
              width: "100%",
              overflow: "auto",
            }}
          >
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <Typography variant="subtitle1">
                {intl.formatMessage({ id: "selectAcademicYear" })}:
              </Typography>
              <select
                value={selectedYear || ""}
                onChange={handleYearChange}
                style={{ padding: "8px", fontSize: "1rem" }}
              >
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
            >
              {statsByClassLevel.map(({ classLevel, stats, totals }) => (
                <Box key={classLevel} sx={{ mt: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {classLevel}.{intl.formatMessage({ id: "class" })}
                  </Typography>
                  <DataGrid
                    rows={stats}
                    columns={columns}
                    autoHeight
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: "#FAFAFA",
                      "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "#ffffff",
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: "1rem",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                      },
                      "& .MuiTablePagination-root": {
                        fontSize: "1rem",
                      },
                    }}
                    hideFooter={stats.length <= 100}
                  />
                  <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="subtitle1">
                          {intl.formatMessage({ id: "totalStudents" })}:{" "}
                          {totals.total}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle1">
                          {intl.formatMessage({ id: "dyslexiaCount" })}:{" "}
                          {totals.dyslexiaCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle1">
                          {intl.formatMessage({ id: "dyslexiaRatio" })}: %
                          {calculatePercentage(
                            totals.dyslexiaCount,
                            totals.validDyslexiaBase
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              ))}

              <Box sx={{ mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {intl.formatMessage({ id: "overallTotal" })}
                </Typography>
                <Paper elevation={3} sx={{ p: 2, mt: 1, mb: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">
                        {intl.formatMessage({ id: "totalStudents" })}:{" "}
                        {overallTotals.total}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">
                        {intl.formatMessage({ id: "dyslexiaCount" })}:{" "}
                        {overallTotals.dyslexiaCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">
                        {intl.formatMessage({ id: "dyslexiaRatio" })}: %
                        {calculatePercentage(
                          overallTotals.dyslexiaCount,
                          overallTotals.validDyslexiaBase
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
      </MiniDrawer>
    </IntlProvider>
  );
};

export default ReportList;
